/**
 * Quiz — an inline multiple-choice quiz component for blog posts.
 *
 * Usage in MDX:
 *   <Quiz
 *     questions={[
 *       {
 *         question: "What does difference() do?",
 *         options: ["Merges shapes", "Subtracts shapes", "Intersects shapes"],
 *         answer: 1,
 *       },
 *     ]}
 *   />
 *
 * Feedback is shown immediately on selection. Once all questions are answered
 * a score summary appears. A Reset button lets the user retry.
 */

import { useState } from 'react';
import { CheckCircle2, XCircle, RotateCcw, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export interface QuizQuestion {
  question: string;
  /** 2–5 answer options */
  options: string[];
  /** 0-based index of the correct option */
  answer: number;
}

export interface QuizProps {
  questions: QuizQuestion[];
  /** Defaults to "Quiz" */
  title?: string;
}

const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E'];

export function Quiz({ questions, title = 'Quiz' }: QuizProps) {
  // Maps question index → chosen option index
  const [answers, setAnswers] = useState<Record<number, number>>({});

  const answeredCount = Object.keys(answers).length;
  const isComplete = answeredCount === questions.length;
  const score = questions.reduce(
    (acc, q, i) => acc + (answers[i] === q.answer ? 1 : 0),
    0,
  );

  function handleSelect(questionIndex: number, optionIndex: number) {
    // Lock after first answer
    if (answers[questionIndex] !== undefined) return;
    setAnswers((prev) => ({ ...prev, [questionIndex]: optionIndex }));
  }

  function handleReset() {
    setAnswers({});
  }

  return (
    <div className="not-prose my-6 rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="text-sm font-semibold text-foreground">{title}</span>
        {isComplete && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" />
            Reset
          </Button>
        )}
      </div>

      {/* Score banner */}
      {isComplete && <ScoreBanner score={score} total={questions.length} />}

      {/* Questions */}
      <div className="divide-y divide-border">
        {questions.map((q, qi) => (
          <QuestionBlock
            key={qi}
            index={qi}
            question={q}
            chosen={answers[qi]}
            onSelect={(oi) => handleSelect(qi, oi)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface ScoreBannerProps {
  score: number;
  total: number;
}

function ScoreBanner({ score, total }: ScoreBannerProps) {
  const ratio = score / total;
  const perfect = ratio === 1;
  const passing = ratio >= 0.5;

  return (
    <div
      className={cn(
        'flex items-center gap-2.5 border-b border-border px-4 py-2.5 text-sm font-medium',
        perfect
          ? 'bg-green-500/10 text-green-700 dark:text-green-400'
          : passing
            ? 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400'
            : 'bg-red-500/10 text-red-700 dark:text-red-400',
      )}
    >
      <Trophy className="h-4 w-4 shrink-0" />
      <span>
        {score} / {total} correct
        {perfect && ' — perfect score!'}
        {!perfect && passing && ' — good effort!'}
        {!perfect && !passing && ' — keep practising!'}
      </span>
    </div>
  );
}

interface QuestionBlockProps {
  index: number;
  question: QuizQuestion;
  /** The option index the user chose, undefined if unanswered */
  chosen: number | undefined;
  onSelect: (optionIndex: number) => void;
}

function QuestionBlock({
  index,
  question,
  chosen,
  onSelect,
}: QuestionBlockProps) {
  const answered = chosen !== undefined;

  return (
    <div className="px-4 py-4">
      {/* Question text */}
      <p className="mb-3 text-sm font-medium text-foreground">
        <span className="mr-1.5 text-muted-foreground">{index + 1}.</span>
        {question.question}
      </p>

      {/* Options */}
      <div className="space-y-2">
        {question.options.map((option, oi) => {
          const isCorrect = oi === question.answer;
          const isChosen = oi === chosen;

          let style: string;
          if (!answered) {
            style =
              'border-border bg-background text-foreground hover:border-primary/60 hover:bg-muted cursor-pointer';
          } else if (isChosen && isCorrect) {
            style =
              'border-green-500 bg-green-500/15 text-green-700 dark:text-green-400 cursor-default';
          } else if (isChosen && !isCorrect) {
            style =
              'border-red-500 bg-red-500/15 text-red-700 dark:text-red-400 cursor-default';
          } else if (!isChosen && isCorrect && answered) {
            // Reveal the correct answer even when the user got it wrong
            style =
              'border-green-500/50 bg-green-500/10 text-green-700/80 dark:text-green-400/80 cursor-default';
          } else {
            style =
              'border-border bg-background text-muted-foreground cursor-default opacity-60';
          }

          return (
            <button
              key={oi}
              onClick={() => onSelect(oi)}
              disabled={answered}
              className={cn(
                'flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                style,
              )}
            >
              {/* Letter label */}
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs font-semibold',
                  !answered && 'bg-muted text-muted-foreground',
                  isChosen &&
                    isCorrect &&
                    'bg-green-500/20 text-green-700 dark:text-green-400',
                  isChosen &&
                    !isCorrect &&
                    'bg-red-500/20 text-red-700 dark:text-red-400',
                  !isChosen &&
                    isCorrect &&
                    answered &&
                    'bg-green-500/15 text-green-700/80 dark:text-green-400/80',
                  !isChosen &&
                    !isCorrect &&
                    answered &&
                    'bg-muted/50 text-muted-foreground',
                )}
              >
                {OPTION_LABELS[oi]}
              </span>

              {/* Option text */}
              <span className="flex-1">{option}</span>

              {/* Feedback icon */}
              {isChosen && isCorrect && (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
              )}
              {isChosen && !isCorrect && (
                <XCircle className="h-4 w-4 shrink-0 text-red-500" />
              )}
              {!isChosen && isCorrect && answered && (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500/60" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
